import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FiUploadCloud, FiX } from 'react-icons/fi';
import { numberMasking } from '../utils/numberMasking';
import './LeadPushModal.scss';

export const leadMobile = value => {
    const digits = String(value ?? '').replace(/[^0-9]/g, '');
    return digits.match(/^(?:0091|91|0)?([6-9][0-9]{9})$/)?.[1] || '';
};
const batchId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join('');
const fields = [['domainname', 'Domain Name'], ['username', 'Username'], ['campname', 'Campaign Name'], ['skillname', 'Skill Name'], ['listname', 'List Name'], ['qname', 'Queue Name']];
const fieldLabel = (label, required = false) => <span>{label}{required && <span className="crm-push-required" aria-hidden="true"> *</span>}</span>;
const placeholders = { domainname: 'Enter domain name', username: 'Enter dialer username', campname: 'Enter campaign name', listname: 'Enter list name',
    skillname: 'Enter skill name', qname: 'Enter queue name' };

export default function LeadPushModal({ show, onClose, rows, api }) {
    const mobiles = useMemo(() => [...new Set(rows.map(row => leadMobile(row.phonenumber)).filter(Boolean))], [rows]);
    const invalid = rows.filter(row => !leadMobile(row.phonenumber)).length;
    const [targets, setTargets] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [retry, setRetry] = useState(0);
    const [form, setForm] = useState({ server: '', domainname: '', username: '', password: '', campname: '', skillname: '', listname: '', qname: '' });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [outcomes, setOutcomes] = useState({});
    const [pushing, setPushing] = useState(false);
    const [retryNotice, setRetryNotice] = useState('');
    const [id, setId] = useState(batchId);
    const stop = useRef(false);
    const running = useRef(false);
    const apiRef = useRef(api);
    apiRef.current = api;
    const target = targets.find(item => item.server === form.server);
    const isVerb = target?.api_type === 'verbLeadPush';
    const activeFields = isVerb
        ? [['domainname', 'Domain Name'], ['campname', 'Campaign Name'], ['skillname', 'Skill Name'], ['listname', 'List Name']]
        : fields;
    const started = Object.keys(outcomes).length > 0;
    const pending = mobiles.filter(mobile => !outcomes[mobile]);
    const unsuccessful = mobiles.filter(mobile => ['failed', 'unknown'].includes(outcomes[mobile]?.outcome));
    const retrying = started && !pending.length && unsuccessful.length > 0;
    const queue = retrying ? unsuccessful : pending;

    useEffect(() => {
        if (!show) return undefined;
        let active = true;
        setOptionsLoading(true); setError('');
        apiRef.current('push-options', {}).then(data => {
            if (!Array.isArray(data)) throw new Error('Push servers could not be loaded');
            if (active) setTargets(data);
        }).catch(failure => { if (active) setError(failure.response?.data?.msg || failure.message || 'Unable to load push servers'); })
            .finally(() => { if (active) setOptionsLoading(false); });
        return () => { active = false; };
    }, [show, retry]);
    useEffect(() => () => { stop.current = true; }, []);

    const chooseTarget = server => {
        const defaults = targets.find(item => item.server === server);
        setForm({ server, password: '', domain_auth_token: '', status: 'NEW',
            ...Object.fromEntries(fields.map(([key]) => [key, defaults?.[key] || ''])) });
        setFieldErrors({}); setError('');
    };
    const updateForm = (key, value) => {
        setForm(current => ({ ...current, [key]: value }));
        setFieldErrors(current => ({ ...current, [key]: undefined }));
        setError('');
    };
    const feedback = key => fieldErrors[key] && <small className="crm-push-field-error" id={`push-error-${key}`}>{fieldErrors[key]}</small>;
    const push = async event => {
        event.preventDefault();
        if (running.current || !queue.length) return;
        if (!target?.available) { setError('Select an available target server.'); return; }
        const requiredFields = isVerb ? ['domainname', 'campname', 'skillname', 'listname', 'status'] : fields.map(([key]) => key);
        const labels = Object.fromEntries([...activeFields, ['status', 'Lead Status'], ['domain_auth_token', 'Domain Auth Token']]);
        const nextErrors = {};
        for (const key of requiredFields) {
            if (!form[key]?.trim()) nextErrors[key] = `${labels[key]} is required.`;
        }
        for (const [key, label] of activeFields) {
            if ((form[key] || '').length > 128) nextErrors[key] = `${label} must be 128 characters or fewer.`;
            else if (Array.from(form[key] || '').some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) nextErrors[key] = `${label} contains invalid characters.`;
        }
        if (isVerb && !form.domain_auth_token && !target.has_saved_token) nextErrors.domain_auth_token = 'Domain Auth Token is required.';
        if (isVerb && form.status.trim() === 'FOLLOWUP') nextErrors.status = 'FOLLOWUP requires a follow-up date. Choose NEW or another status.';
        setFieldErrors(nextErrors);
        if (Object.keys(nextErrors).length) { setError('Please complete the highlighted fields before pushing leads.'); return; }
        // An explicit retry starts a fresh attempt for unsuccessful mobiles only.
        // Keep one key through automatic retries; accepted/skipped mobiles stay excluded.
        const requestBatch = retrying ? batchId() : id;
        if (retrying) setId(requestBatch);
        const payload = isVerb ? Object.fromEntries(['server', 'domainname', 'domain_auth_token', 'campname', 'skillname', 'listname', 'status']
            .map(key => [key, form[key]])) : { ...form };
        running.current = true; stop.current = false; setPushing(true); setError(''); setRetryNotice('');
        try {
            for (const mobile of queue) {
                if (stop.current) break;
                let result;
                const automatic = target.api_type === 'manualUpload';
                for (let attempt = 1; attempt <= 3; attempt += 1) {
                    if (stop.current) break;
                    let retryable = false;
                    try {
                        result = await apiRef.current('push-lead', { ...payload, mobile, batchId: requestBatch });
                        if (!['success', 'failed', 'skipped', 'unknown'].includes(result?.outcome)) {
                            result = { outcome: 'unknown', message: 'No confirmation. Check the target server before retrying.' };
                        }
                        retryable = result.outcome === 'unknown' && result.retryable === true;
                    } catch (failure) {
                        const http = failure.response?.status;
                        if (http === 401 || http === 403) stop.current = true;
                        retryable = !http || [408, 409, 425, 429, 500, 502, 503, 504].includes(http);
                        result = { outcome: 'unknown', message: failure.response?.data?.msg || failure.message || 'No confirmation. Check target server.' };
                    }
                    if (!automatic || !retryable || attempt === 3 || stop.current) break;
                    setRetryNotice(`Temporary connection issue. Retrying ${numberMasking(mobile)} (attempt ${attempt + 1} of 3)...`);
                    // Keep the same batch/mobile key: a delayed response cannot cause a duplicate upload.
                    await new Promise(resolve => setTimeout(resolve, attempt * 2000));
                }
                setRetryNotice('');
                if (result) setOutcomes(current => ({ ...current, [mobile]: result }));
                // Verb retains pause-on-uncertain behavior; NGUCC continues with the next number.
                if (!automatic && result?.outcome === 'unknown') break;
            }
        } finally { running.current = false; setPushing(false); setRetryNotice(''); }
    };

    return <Modal show={show} onHide={() => { if (!pushing) onClose(); }} centered size="lg" backdrop="static"
        keyboard={!pushing} className="crm-lead-push-modal" backdropClassName="crm-lead-push-backdrop" aria-labelledby="lead-push-title">
        <Modal.Header><div className="crm-push-heading"><span className="crm-push-heading-icon"><FiUploadCloud aria-hidden="true" /></span>
            <div><Modal.Title id="lead-push-title">Push Unmatched Leads</Modal.Title><p>Choose your server and prepare your leads.</p></div></div>
            <button type="button" aria-label="Close push leads" disabled={pushing} onClick={onClose}><FiX aria-hidden="true" /></button>
        </Modal.Header>
        <form onSubmit={push} noValidate>
            <Modal.Body>
                <p className="crm-push-count">{mobiles.length.toLocaleString('en-IN')} unique mobile numbers from {rows.length.toLocaleString('en-IN')} unmatched calls</p>
                {target?.api_type === 'manualUpload' && <p className="crm-push-auto-note">Start once to process all pending leads automatically. Temporary connection issues get up to 3 attempts.</p>}
                {invalid > 0 && <p>{invalid} calls with invalid mobile numbers will be skipped.</p>}
                <fieldset disabled={pushing || optionsLoading}>
                    <div className="crm-push-fields">
                        <label>{fieldLabel('Target Server', true)}<select required disabled={started} aria-label="Target Server" value={form.server} onChange={event => chooseTarget(event.target.value)}>
                            <option value="">{optionsLoading ? 'Loading servers...' : 'Select target server'}</option>
                            {targets.map(item => <option key={item.server} value={item.server}>{item.label}</option>)}
                        </select></label>
                        <label>{isVerb ? 'API Endpoint' : 'Action (do)'}<input value={isVerb ? '/admin/leadpush' : target?.api_type || ''} placeholder="Select a server first" readOnly /></label>
                        {activeFields.map(([key, label]) => <label key={key}>{fieldLabel(label, true)}<input required disabled={!target?.available} maxLength={128} value={form[key] || ''}
                            placeholder={placeholders[key]}
                            aria-label={label} aria-invalid={!!fieldErrors[key]} aria-describedby={fieldErrors[key] ? `push-error-${key}` : undefined}
                            onChange={event => updateForm(key, event.target.value)} />{feedback(key)}</label>)}
                        {isVerb ? (!target?.has_saved_token && <label>{fieldLabel('Domain Auth Token', true)}<input type="password" disabled={!target?.available} autoComplete="new-password" required maxLength={2048} value={form.domain_auth_token || ''}
                            aria-label="Domain Auth Token" aria-invalid={!!fieldErrors.domain_auth_token} aria-describedby={fieldErrors.domain_auth_token ? 'push-error-domain_auth_token' : undefined}
                            placeholder="Enter domain auth token" onChange={event => updateForm('domain_auth_token', event.target.value)} />{feedback('domain_auth_token')}</label>)
                            : <label>Password<input type="password" disabled={!target?.available} autoComplete="new-password" maxLength={128} value={form.password}
                            placeholder="Use saved server password" onChange={event => setForm(current => ({ ...current, password: event.target.value }))} /></label>
                        }
                        <label>{isVerb ? 'Mobile (phonenumber)' : 'Mobile (phone1)'}<input value={`${mobiles.length} unmatched mobile numbers (automatic)`} readOnly /></label>
                        {isVerb ? <>
                            <label>{fieldLabel('Lead Status', true)}<input required placeholder="e.g. NEW or DND" maxLength={128} value={form.status ?? 'NEW'} aria-label="Lead Status" aria-invalid={!!fieldErrors.status}
                                aria-describedby={fieldErrors.status ? 'push-error-status' : undefined} onChange={event => updateForm('status', event.target.value.toUpperCase())} />{feedback('status')}</label>
                        </> : <label>Lead Status<input value="NEW" readOnly /></label>}
                    </div>
                </fieldset>
                {target && !target.available && <p className="crm-push-error" role="status">{target.unavailableReason || 'API setup pending for this server.'}</p>}
                {error && <p className="crm-push-error" role="alert">{error}
                    {!targets.length && <button type="button" disabled={optionsLoading} onClick={() => setRetry(value => value + 1)}>Retry servers</button>}</p>}
                {retryNotice && <p className="crm-push-progress" role="status">{retryNotice}</p>}
                {started && <div className="crm-push-progress" role="status">
                    {Object.keys(outcomes).length} of {mobiles.length} processed · {Object.values(outcomes).filter(item => item.outcome === 'success').length} accepted · {pending.length} pending
                </div>}
                {started && <div className="crm-push-results"><table><thead><tr><th>Mobile</th><th>Result</th><th>Details</th></tr></thead>
                    <tbody>{Object.entries(outcomes).map(([mobile, result]) => <tr key={mobile}><td>{numberMasking(mobile)}</td>
                        <td>{result.outcome}</td><td>{result.message}
                            {result.response_preview && result.outcome !== 'success' && <details className="crm-push-response"><summary>Dialer response (HTTP {result.http_code})</summary>
                                <pre>{result.response_preview}</pre>
                                {result.request_method && <p>Request: {result.request_method} {result.target_host}{result.target_port ? `:${result.target_port}` : ''}{result.target_path}</p>}
                                {result.remote_ip && <p>Connected IP: {result.remote_ip}</p>}
                                {result.response_server && <p>Response server: {result.response_server}</p>}
                                {result.transport_code > 0 && <span>Connection code: {result.transport_code}</span>}
                            </details>}
                        </td></tr>)}</tbody></table></div>}
            </Modal.Body>
            <Modal.Footer>
                {pushing ? <button type="button" onClick={() => { stop.current = true; }}>Stop after current lead</button>
                    : <button type="button" onClick={onClose}>Close</button>}
                <button type="submit" className="crm-push-submit" disabled={pushing || optionsLoading || !target?.available || !queue.length}>
                    {pushing ? 'Pushing Leads...' : retrying ? `Retry ${unsuccessful.length} Unsuccessful Leads` : started && !pending.length ? 'Batch Complete' : started ? `Continue ${pending.length} Pending Leads` : `Push ${mobiles.length} Leads`}
                </button>
            </Modal.Footer>
        </form>
    </Modal>;
}
